import { useCallback, useState } from "react";

import { DateTime } from "luxon";
import { useDebouncedMutation } from "../../../../common/Utils/useDebouncedMutation";
import { graphql, useMutation } from "react-relay";
import { useTodoEditSetExpireAtMutation } from "./__generated__/useTodoEditSetExpireAtMutation.graphql";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";

export function useTodoEditSetExpireAt(id: string, remoteExpireAt?: string) {
  const { currentEmployeeId } = usePersistedEmployeeId();
  const [updateExpireAt, isInFlight] =
    useMutation<useTodoEditSetExpireAtMutation>(graphql`
      mutation useTodoEditSetExpireAtMutation(
        $id: ID!
        $expireAt: String
        $employeeId: ID
      ) {
        todo(todoId: $id, employeeId: $employeeId) {
          updateScheduling(newExpireAt: $expireAt) {
            id

            expireAtDate {
              ...StoreFormattedDate
              isoString
            }
            ...TodoEditTimeline_todo @defer
          }
        }
      }
    `);

  const [error, setError] = useState<Error | null>(null);

  const saveExpireAt = useCallback(
    (expireAt?: string) => {
      updateExpireAt({
        onError: setError,
        onCompleted: () => setError(null),
        variables: {
          id,
          expireAt,
          employeeId: currentEmployeeId,
        },
      });
    },
    [id, updateExpireAt, currentEmployeeId]
  );

  const [expireAt, setExpireAt, dirty] = useDebouncedMutation(
    remoteExpireAt,
    saveExpireAt
  );

  const setExpireAtDate = useCallback(
    (expireAt?: Date) => {
      setExpireAt(expireAt?.toISOString());
    },
    [setExpireAt]
  );

  return [
    expireAt ? DateTime.fromISO(expireAt).toJSDate() : undefined,
    setExpireAtDate,
    dirty,
    isInFlight,
    error,
  ] as const;
}
